import React, { useState, useEffect } from 'react';
import EgRegressionChart from './EgRegressionChart';
import AnalysisPieChart from './AnalysisPieChart';
import ZeitstrahlWaBlock from './ZeitstrahlWaBlock';
import AnalysisDashboard from './AnalysisDashboard';
import TimePill from './TimePill';

const KUERZEL_PALETTE_LIGHT = ['#3b82f6', '#f59e0b', '#10b981', '#ec4899', '#8b5cf6', '#06b6d4', '#ef4444', '#eab308', '#22c55e', '#a855f7', '#0ea5e9', '#f97316', '#14b8a6', '#d946ef'];
const KUERZEL_PALETTE_DARK = ['#60a5fa', '#fbbf24', '#34d399', '#f472b6', '#a78bfa', '#22d3ee', '#fb7185', '#facc15', '#4ade80', '#c084fc', '#38bdf8', '#fb923c', '#2dd4bf', '#e879f9'];

function deterministicColor(key, palette) {
  if (!key) return palette[0];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = ((h << 5) - h + key.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length];
}

function loadChartType(storageKey, fallback = 'tabelle') {
  try {
    const v = localStorage.getItem(storageKey);
    if (v === 'tabelle' || v === 'balken' || v === 'kreis') return v;
  } catch { /* ignore */ }
  return fallback;
}

function loadTimeMode(storageKey, fallback = 'vergleich') {
  try {
    const v = localStorage.getItem(storageKey);
    if (v === 'ist' || v === 'soll' || v === 'vergleich') return v;
  } catch { /* ignore */ }
  return fallback;
}

function AnalysisPanel(props) {
  const {
    pivotDrillDown, setPivotDrillDown,
    analysisViewMode, setAnalysisViewMode,
    statistikGroupBy, setStatistikGroupBy,
    statistikDisplay, setStatistikDisplay,
    statistikTimeType, setStatistikTimeType,
    eaDrillDown, setEaDrillDown,
    zeitstrahlExpandedWaNr, setZeitstrahlExpandedWaNr,
    zeitstrahlHover, setZeitstrahlHover,
    zeitstrahlHoverTimeoutRef,
    setPhotoPreview,
    timeTypes, defaultTimeTypes, ablaufDbData,
    measurementPhotos,
    darkMode, decimalPlacesSollzeiten,
    formatTime, formatGrade, formatSollzeit, calculateAvgGrade, computeStats,
    analyzeEinflussgroessenZeitbestimmend, simpleOLSWithBand,
    buildDesignMatrixEA, multilinearOLS, kMeans,
    getAnalysis,
    t,
  } = props;

  const [bilanzZeitartenChartType, setBilanzZeitartenChartType] = useState(() => loadChartType('bilanzZeitartenChartType', 'tabelle'));
  const [bilanzKuerzelChartType, setBilanzKuerzelChartType] = useState(() => loadChartType('bilanzKuerzelChartType', 'tabelle'));
  useEffect(() => { try { localStorage.setItem('bilanzZeitartenChartType', bilanzZeitartenChartType); } catch { /* ignore */ } }, [bilanzZeitartenChartType]);
  useEffect(() => { try { localStorage.setItem('bilanzKuerzelChartType', bilanzKuerzelChartType); } catch { /* ignore */ } }, [bilanzKuerzelChartType]);

  // IST/SOLL/Vergleich-Toggle pro Bilanz-Tab; nur fuer Balken und Kreis relevant (Tabelle zeigt eh beides).
  const [bilanzZeitartenTimeMode, setBilanzZeitartenTimeMode] = useState(() => loadTimeMode('bilanzZeitartenTimeMode', 'vergleich'));
  const [bilanzKuerzelTimeMode, setBilanzKuerzelTimeMode] = useState(() => loadTimeMode('bilanzKuerzelTimeMode', 'vergleich'));
  useEffect(() => { try { localStorage.setItem('bilanzZeitartenTimeMode', bilanzZeitartenTimeMode); } catch { /* ignore */ } }, [bilanzZeitartenTimeMode]);
  useEffect(() => { try { localStorage.setItem('bilanzKuerzelTimeMode', bilanzKuerzelTimeMode); } catch { /* ignore */ } }, [bilanzKuerzelTimeMode]);

  // Origin des Drilldown: 'dashboard' wenn aus einer Dashboard-Kachel geoeffnet, sonst 'tab'.
  // Bei Zurueck wird abhaengig davon entweder zum Dashboard zurueckgesprungen oder im Tab geblieben.
  const [analysisDrillOrigin, setAnalysisDrillOrigin] = useState('tab');
  const closeDrillDown = () => {
    const wasDashboard = analysisDrillOrigin === 'dashboard';
    setPivotDrillDown(null);
    setEaDrillDown(null);
    setAnalysisDrillOrigin('tab');
    if (wasDashboard) setAnalysisViewMode('dashboard');
  };
  // Bei manuellem Tab-Wechsel ueber die Tab-Bar Origin auf 'tab' zuruecksetzen,
  // damit ein anschliessender Drilldown-Klick im Tab nicht versehentlich als Dashboard-Origin gewertet wird.
  const switchAnalysisTab = (mode) => {
    setAnalysisDrillOrigin('tab');
    setAnalysisViewMode(mode);
  };

  const palette = darkMode ? KUERZEL_PALETTE_DARK : KUERZEL_PALETTE_LIGHT;

  const colorForTimeType = (name) => {
    const fromConfig = (Array.isArray(timeTypes) ? timeTypes.find(tt => tt.name === name) : null)?.color;
    const fromDefaults = (Array.isArray(defaultTimeTypes) ? defaultTimeTypes.find(tt => tt.name === name) : null)?.color;
    return fromConfig || fromDefaults || (name === 'Keine Zeitart' ? (darkMode ? '#94a3b8' : '#94a3b8') : null) || deterministicColor(String(name || ''), palette);
  };
  const colorForKuerzel = (name) => {
    if (name === 'Kein Kürzel') return darkMode ? '#6b7280' : '#9ca3af';
    return deterministicColor(String(name || ''), palette);
  };
  const renderChartTypeToggle = (current, onChange, idPrefix, timeMode, setTimeMode) => (
    <div className="analysis-chart-type-toggle" style={{ display: 'flex', gap: '8px', marginTop: '12px', marginBottom: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontWeight: 600, color: darkMode ? '#f9fafb' : '#111827', fontSize: '13px' }}>Darstellung:</span>
      {[
        { id: 'tabelle', label: 'Tabelle' },
        { id: 'balken', label: 'Balken' },
        { id: 'kreis', label: 'Kreis' },
      ].map(opt => (
        <button
          key={opt.id}
          type="button"
          className={`btn-analysis-mode ${current === opt.id ? 'active' : ''}`}
          onClick={() => onChange(opt.id)}
          style={{
            padding: '6px 12px',
            fontSize: '13px',
            border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
            borderRadius: '4px',
            backgroundColor: current === opt.id ? '#667eea' : 'transparent',
            color: current === opt.id ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
            cursor: 'pointer',
            fontWeight: current === opt.id ? 'bold' : 'normal'
          }}
          data-test={`${idPrefix}-${opt.id}`}
        >{opt.label}</button>
      ))}
      {setTimeMode && current !== 'tabelle' && (
        <>
          <span style={{ marginLeft: '12px', fontWeight: 600, color: darkMode ? '#f9fafb' : '#111827', fontSize: '13px' }}>Zeit:</span>
          <TimePill
            value={timeMode}
            onChange={setTimeMode}
            darkMode={darkMode}
            ariaLabel="Zeit-Modus"
            options={[
              { id: 'ist', label: 'IST', title: 'Nur IST-Werte' },
              { id: 'soll', label: 'SOLL', title: 'Nur SOLL-Werte (Vorgabezeit)' },
              { id: 'vergleich', label: 'Vergleich', title: 'IST und SOLL gegenueberstellen' },
            ]}
          />
        </>
      )}
    </div>
  );

          const analysis = getAnalysis();
          const activities = Object.keys(analysis.pivot).sort();
          const pivotTimeTypeNames = [...new Set(Object.values(analysis.pivot).flatMap(activity => Object.keys(activity)))].sort();
          
          // Berechne Gesamtzeilen und -spalten
          const rowTotals = {};
          const colTotals = {};
          let grandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
          
          activities.forEach(activity => {
            rowTotals[activity] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
            Object.values(analysis.pivot[activity]).forEach(cell => {
              rowTotals[activity].count += cell.count;
              rowTotals[activity].totalTime += cell.totalTime;
              rowTotals[activity].totalVorgabeTime += (cell.totalVorgabeTime || 0);
              rowTotals[activity].grades.push(...cell.grades);
              grandTotal.count += cell.count;
              grandTotal.totalTime += cell.totalTime;
              grandTotal.totalVorgabeTime += (cell.totalVorgabeTime || 0);
              grandTotal.grades.push(...cell.grades);
            });
            rowTotals[activity].avgGrade = calculateAvgGrade(rowTotals[activity].grades);
          });
          
          pivotTimeTypeNames.forEach(timeType => {
            colTotals[timeType] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
            activities.forEach(activity => {
              if (analysis.pivot[activity][timeType]) {
                const cell = analysis.pivot[activity][timeType];
                colTotals[timeType].count += cell.count;
                colTotals[timeType].totalTime += cell.totalTime;
                colTotals[timeType].totalVorgabeTime += (cell.totalVorgabeTime || 0);
                colTotals[timeType].grades.push(...cell.grades);
              }
            });
            colTotals[timeType].avgGrade = calculateAvgGrade(colTotals[timeType].grades);
          });
          
          // eslint-disable-next-line no-unused-vars
          const grandAvgGrade = calculateAvgGrade(grandTotal.grades);
          
          return (
            <div className="analysis-panel">
              <div className="analysis-header">
                <h3>{t('common.auswertung_with_icon')}</h3>
                {pivotDrillDown && (
                  <button 
                    className="btn-close-drilldown" 
                    onClick={closeDrillDown}
                    title={analysisDrillOrigin === 'dashboard' ? 'Zurück zum Dashboard' : 'Drill-Down schließen'}
                  >
                    ✕ Zurück
                  </button>
                )}
                {!pivotDrillDown && (
                  <div className="analysis-view-mode" style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                    <button
                      className={`btn-analysis-mode ${analysisViewMode === 'dashboard' ? 'active' : ''}`}
                      onClick={() => switchAnalysisTab('dashboard')}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: analysisViewMode === 'dashboard' ? '#667eea' : 'transparent',
                        color: analysisViewMode === 'dashboard' ? 'white' : (darkMode ? '#f9fafb' : '#111827'),
                        cursor: 'pointer',
                        fontWeight: analysisViewMode === 'dashboard' ? 'bold' : 'normal'
                      }}
                      title="Dashboard: Übersicht aller Auswertungen als Kacheln (Bilanz Zeitarten als Kreis, Bilanz Kürzel als Balken, Zeitstrahl pro WA, Pivot, Statistik)"
                    >
                      📊 Dashboard
                    </button>
                    <button
                      className={`btn-analysis-mode ${analysisViewMode === 'timeType' ? 'active' : ''}`}
                      onClick={() => switchAnalysisTab('timeType')}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: analysisViewMode === 'timeType' ? '#667eea' : 'transparent',
                        color: analysisViewMode === 'timeType' ? 'white' : (darkMode ? '#f9fafb' : '#111827'),
                        cursor: 'pointer',
                        fontWeight: analysisViewMode === 'timeType' ? 'bold' : 'normal'
                      }}
                      title="Bilanz nach Zeitarten"
                    >
                      Bilanz Zeitarten
                    </button>
                    <button
                      className={`btn-analysis-mode ${analysisViewMode === 'kuerzel' ? 'active' : ''}`}
                      onClick={() => switchAnalysisTab('kuerzel')}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: analysisViewMode === 'kuerzel' ? '#667eea' : 'transparent',
                        color: analysisViewMode === 'kuerzel' ? 'white' : (darkMode ? '#f9fafb' : '#111827'),
                        cursor: 'pointer',
                        fontWeight: analysisViewMode === 'kuerzel' ? 'bold' : 'normal'
                      }}
                      title="Bilanz nach Kürzel"
                    >
                      Bilanz Kürzel
                    </button>
                    <button
                      className={`btn-analysis-mode ${analysisViewMode === 'zeitstrahl' ? 'active' : ''}`}
                      onClick={() => switchAnalysisTab('zeitstrahl')}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: analysisViewMode === 'zeitstrahl' ? '#667eea' : 'transparent',
                        color: analysisViewMode === 'zeitstrahl' ? 'white' : (darkMode ? '#f9fafb' : '#111827'),
                        cursor: 'pointer',
                        fontWeight: analysisViewMode === 'zeitstrahl' ? 'bold' : 'normal'
                      }}
                      title="Zeitstrahl: Pro WA-Nr aufklappbar; Hover: Beschreibung, Notiz, EA, Fotos"
                    >
                      Zeitstrahl
                    </button>
                    <button
                      className={`btn-analysis-mode ${analysisViewMode === 'pivot' ? 'active' : ''}`}
                      onClick={() => switchAnalysisTab('pivot')}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: analysisViewMode === 'pivot' ? '#667eea' : 'transparent',
                        color: analysisViewMode === 'pivot' ? 'white' : (darkMode ? '#f9fafb' : '#111827'),
                        cursor: 'pointer',
                        fontWeight: analysisViewMode === 'pivot' ? 'bold' : 'normal'
                      }}
                      title="Pivot-Tabelle"
                    >
                      Pivot-Tabelle
                    </button>
                    <button
                      className={`btn-analysis-mode ${analysisViewMode === 'statistik' ? 'active' : ''}`}
                      onClick={() => switchAnalysisTab('statistik')}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: analysisViewMode === 'statistik' ? '#667eea' : 'transparent',
                        color: analysisViewMode === 'statistik' ? 'white' : (darkMode ? '#f9fafb' : '#111827'),
                        cursor: 'pointer',
                        fontWeight: analysisViewMode === 'statistik' ? 'bold' : 'normal'
                      }}
                      title="Statistik, Regression, Clustering"
                    >
                      Statistik
                    </button>
                  </div>
                )}
              </div>
              {!pivotDrillDown ? (
                <>
                  {/* Dashboard: Uebersicht aller Auswertungen als Kacheln */}
                  {analysisViewMode === 'dashboard' && (
                    <AnalysisDashboard
                      analysis={analysis}
                      timeTypes={timeTypes}
                      defaultTimeTypes={defaultTimeTypes}
                      ablaufDbData={ablaufDbData}
                      darkMode={darkMode}
                      decimalPlacesSollzeiten={decimalPlacesSollzeiten}
                      formatTime={formatTime}
                      formatSollzeit={formatSollzeit}
                      setAnalysisViewMode={setAnalysisViewMode}
                      setPivotDrillDown={setPivotDrillDown}
                      setEaDrillDown={setEaDrillDown}
                      setStatistikGroupBy={setStatistikGroupBy}
                      computeStats={computeStats}
                      setAnalysisDrillOrigin={setAnalysisDrillOrigin}
                    />
                  )}

                  {/* Statistik: Min, Max, Mittelwert, Std, Varianz, Variation; Kürzel / Kürzel+E; Tabelle / Balken; Regression, Clustering */}
                  {analysisViewMode === 'statistik' && (() => {
                    const groupData = statistikGroupBy === 'kuerzel' ? (analysis.byKuerzel || {}) : (statistikGroupBy === 'kuerzelE' ? (analysis.byKuerzelE || {}) : (analysis.byKuerzelEA || {}));
                    // Berechne Statistik dynamisch basierend auf statistikTimeType
                    const computedGroupData = {};
                    Object.keys(groupData).forEach(k => {
                      const g = groupData[k];
                      const times = (g.measurements || []).map(mm => 
                        statistikTimeType === 'soll' ? (mm.vorgabeTime || 0) : (mm.individualTime || 0)
                      );
                      computedGroupData[k] = {
                        ...g,
                        ...computeStats(times)
                      };
                    });
                    const groupKeys = Object.keys(computedGroupData).sort((a, b) => {
                      if (a === 'Kein Kürzel' || a.startsWith('Kein Kürzel')) return 1;
                      if (b === 'Kein Kürzel' || b.startsWith('Kein Kürzel')) return -1;
                      return a.localeCompare(b);
                    });
                    const maxMean = groupKeys.length ? Math.max(...groupKeys.map(k => computedGroupData[k].mean || 0)) : 1;

                    return (
                      <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '16px', marginBottom: '12px', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600, color: darkMode ? '#f9fafb' : '#111827' }}>Gruppierung:</span>
                          <button
                            className={`btn-analysis-mode ${statistikGroupBy === 'kuerzel' ? 'active' : ''}`}
                            onClick={() => setStatistikGroupBy('kuerzel')}
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '13px',
                              border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
                              borderRadius: '4px',
                              backgroundColor: statistikGroupBy === 'kuerzel' ? '#667eea' : 'transparent',
                              color: statistikGroupBy === 'kuerzel' ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
                              cursor: 'pointer',
                              fontWeight: statistikGroupBy === 'kuerzel' ? 'bold' : 'normal'
                            }}
                          >Kürzel</button>
                          <button
                            className={`btn-analysis-mode ${statistikGroupBy === 'kuerzelE' ? 'active' : ''}`}
                            onClick={() => setStatistikGroupBy('kuerzelE')}
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '13px',
                              border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
                              borderRadius: '4px',
                              backgroundColor: statistikGroupBy === 'kuerzelE' ? '#667eea' : 'transparent',
                              color: statistikGroupBy === 'kuerzelE' ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
                              cursor: 'pointer',
                              fontWeight: statistikGroupBy === 'kuerzelE' ? 'bold' : 'normal'
                            }}
                          >Kürzel + E</button>
                          <button
                            className={`btn-analysis-mode ${statistikGroupBy === 'kuerzelEA' ? 'active' : ''}`}
                            onClick={() => setStatistikGroupBy('kuerzelEA')}
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '13px',
                              border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
                              borderRadius: '4px',
                              backgroundColor: statistikGroupBy === 'kuerzelEA' ? '#667eea' : 'transparent',
                              color: statistikGroupBy === 'kuerzelEA' ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
                              cursor: 'pointer',
                              fontWeight: statistikGroupBy === 'kuerzelEA' ? 'bold' : 'normal'
                            }}
                          >Kürzel + EA</button>
                          <span style={{ marginLeft: '8px', fontWeight: 600, color: darkMode ? '#f9fafb' : '#111827' }}>Darstellung:</span>
                          <button
                            className={`btn-analysis-mode ${statistikDisplay === 'table' ? 'active' : ''}`}
                            onClick={() => setStatistikDisplay('table')}
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '13px',
                              border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
                              borderRadius: '4px',
                              backgroundColor: statistikDisplay === 'table' ? '#667eea' : 'transparent',
                              color: statistikDisplay === 'table' ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
                              cursor: 'pointer',
                              fontWeight: statistikDisplay === 'table' ? 'bold' : 'normal'
                            }}
                          >Tabelle</button>
                          <button
                            className={`btn-analysis-mode ${statistikDisplay === 'bar' ? 'active' : ''}`}
                            onClick={() => setStatistikDisplay('bar')}
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '13px',
                              border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
                              borderRadius: '4px',
                              backgroundColor: statistikDisplay === 'bar' ? '#667eea' : 'transparent',
                              color: statistikDisplay === 'bar' ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
                              cursor: 'pointer',
                              fontWeight: statistikDisplay === 'bar' ? 'bold' : 'normal'
                            }}
                          >Balken</button>
                          <button
                            className={`btn-analysis-mode ${statistikDisplay === 'kreis' ? 'active' : ''}`}
                            onClick={() => setStatistikDisplay('kreis')}
                            style={{
                              padding: '6px 12px',
                              fontSize: '13px',
                              border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
                              borderRadius: '4px',
                              backgroundColor: statistikDisplay === 'kreis' ? '#667eea' : 'transparent',
                              color: statistikDisplay === 'kreis' ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
                              cursor: 'pointer',
                              fontWeight: statistikDisplay === 'kreis' ? 'bold' : 'normal'
                            }}
                            title="Kreisdiagramm: Anteile an der Gesamtzeit (Summe je Gruppe)"
                          >Kreis</button>
                          <span style={{ marginLeft: '8px', fontWeight: 600, color: darkMode ? '#f9fafb' : '#111827' }}>Zeit:</span>
                          <button
                            className={`btn-analysis-mode ${statistikTimeType === 'soll' ? 'active' : ''}`}
                            onClick={() => setStatistikTimeType('soll')}
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '13px',
                              border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
                              borderRadius: '4px',
                              backgroundColor: statistikTimeType === 'soll' ? '#667eea' : 'transparent',
                              color: statistikTimeType === 'soll' ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
                              cursor: 'pointer',
                              fontWeight: statistikTimeType === 'soll' ? 'bold' : 'normal'
                            }}
                          >Soll (min)</button>
                          <button
                            className={`btn-analysis-mode ${statistikTimeType === 'ist' ? 'active' : ''}`}
                            onClick={() => setStatistikTimeType('ist')}
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '13px',
                              border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
                              borderRadius: '4px',
                              backgroundColor: statistikTimeType === 'ist' ? '#667eea' : 'transparent',
                              color: statistikTimeType === 'ist' ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
                              cursor: 'pointer',
                              fontWeight: statistikTimeType === 'ist' ? 'bold' : 'normal'
                            }}
                          >IST (HM)</button>
                        </div>

                        {statistikDisplay === 'table' && (
                          <div className="analysis-table-wrapper" style={{ marginTop: '8px' }}>
                            <table className="analysis-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                              <thead>
                                <tr style={{ backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{statistikGroupBy === 'kuerzel' ? 'Kürzel' : (statistikGroupBy === 'kuerzelE' ? 'Kürzel + E' : 'Kürzel + EA')}</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>n</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Min</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Max</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Mittelwert</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Std</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Varianz</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Spannweite</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Var.-Koeff. %</th>
                                </tr>
                              </thead>
                              <tbody>
                                {groupKeys.map(k => {
                                  const d = computedGroupData[k];
                                  const egKeysKuerzel = [...new Set((d.measurements || []).flatMap(m => (m.einflussgroessen && typeof m.einflussgroessen === 'object') ? Object.keys(m.einflussgroessen) : []))];
                                  const isClickable = (statistikGroupBy === 'kuerzel' || statistikGroupBy === 'kuerzelE' || statistikGroupBy === 'kuerzelEA');
                                  // Formatierung basierend auf statistikTimeType
                                  const formatValue = (value) => {
                                    if (d.n === 0 || value == null) return '–';
                                    if (statistikTimeType === 'soll') {
                                      return formatSollzeit(value, decimalPlacesSollzeiten) + ' min';
                                    } else {
                                      return formatTime(value);
                                    }
                                  };
                                  return (
                                    <tr
                                      key={k}
                                      onClick={isClickable ? () => setEaDrillDown({ key: k, kuerzel: k, egList: egKeysKuerzel, selectedEG: egKeysKuerzel[0] || null }) : undefined}
                                      style={{ cursor: isClickable ? 'pointer' : 'default' }}
                                      title={isClickable ? (egKeysKuerzel.length ? 'Klicken: Einzelanalyse (Regression, Trompetenkurve)' : '') : undefined}
                                    >
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', fontWeight: 500, color: darkMode ? '#e5e7eb' : '#111827' }} title={k}>{k.length > 36 ? k.slice(0, 33) + '…' : k}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{d.count}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatValue(d.min)}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatValue(d.max)}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatValue(d.mean)}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatValue(d.std)}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatValue(d.var)}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatValue(d.spanne)}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{d.varKoeff != null ? d.varKoeff.toFixed(1) + ' %' : '–'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {statistikDisplay === 'bar' && (
                          <div style={{ marginTop: '12px' }}>
                            {groupKeys.map(k => {
                              const d = computedGroupData[k];
                              const pct = maxMean > 0 ? (d.mean / maxMean) * 100 : 0;
                              const formatValue = (value) => {
                                if (d.n === 0 || value == null) return '–';
                                if (statistikTimeType === 'soll') {
                                  return formatSollzeit(value, decimalPlacesSollzeiten) + ' min';
                                } else {
                                  return formatTime(value);
                                }
                              };
                              return (
                                <div key={k} style={{ marginBottom: '10px' }}>
                                  <div style={{ fontSize: '12px', marginBottom: '2px', color: darkMode ? '#d1d5db' : '#374151' }} title={k}>{k.length > 40 ? k.slice(0, 37) + '…' : k} – Ø {formatValue(d.mean)}</div>
                                  <div style={{ height: '20px', background: darkMode ? '#374151' : '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: pct + '%', background: 'linear-gradient(90deg, #667eea, #764ba2)', borderRadius: '4px', minWidth: d.n > 0 ? '4px' : 0 }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {statistikDisplay === 'kreis' && (() => {
                          const isClickable = (statistikGroupBy === 'kuerzel' || statistikGroupBy === 'kuerzelE' || statistikGroupBy === 'kuerzelEA');
                          const slices = groupKeys.map(k => {
                            const g = computedGroupData[k];
                            const value = statistikTimeType === 'soll' ? (g.totalVorgabeTime || 0) : (g.totalTime || 0);
                            return { key: k, label: k, value, count: g.count || 0, color: deterministicColor(String(k), palette) };
                          });
                          const totalSum = slices.reduce((s, x) => s + (x.value || 0), 0);
                          const formatValue = (v) => statistikTimeType === 'soll' ? (formatSollzeit(v, decimalPlacesSollzeiten) + ' min') : formatTime(v);
                          return (
                            <div style={{ marginTop: '12px' }}>
                              <AnalysisPieChart
                                slices={slices}
                                formatValue={formatValue}
                                centerValue={formatValue(totalSum)}
                                centerLabel={statistikTimeType === 'soll' ? 'Summe SOLL' : 'Summe IST'}
                                darkMode={darkMode}
                                size={240}
                                ariaLabel="Statistik Kreisdiagramm"
                                onSliceClick={isClickable ? (key) => {
                                  const g = computedGroupData[key];
                                  if (!g) return;
                                  const egKeysKuerzel = [...new Set((g.measurements || []).flatMap(m => (m.einflussgroessen && typeof m.einflussgroessen === 'object') ? Object.keys(m.einflussgroessen) : []))];
                                  setEaDrillDown({ key, kuerzel: key, egList: egKeysKuerzel, selectedEG: egKeysKuerzel[0] || null });
                                } : undefined}
                              />
                            </div>
                          );
                        })()}

                        {/* Einzelanalyse (Regression): bei Kürzel, Kürzel+E und Kürzel+EA, aus Klick auf Statistik-Zeile */}
                        {eaDrillDown && (statistikGroupBy === 'kuerzel' || statistikGroupBy === 'kuerzelE' || statistikGroupBy === 'kuerzelEA') && (
                          <div style={{ marginTop: '24px', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: darkMode ? '#1f2937' : '#f9fafb' }}>
                            <button type="button" onClick={closeDrillDown} style={{ marginBottom: '12px', padding: '6px 12px', cursor: 'pointer' }} title={analysisDrillOrigin === 'dashboard' ? 'Zurück zum Dashboard' : 'Einzelanalyse schließen'}>← Zurück</button>
                            <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: darkMode ? '#f9fafb' : '#111827' }}>Einzelanalyse: {statistikGroupBy === 'kuerzel' ? 'Kürzel' : (statistikGroupBy === 'kuerzelE' ? 'Kürzel+E' : 'Kürzel+EA')} „{eaDrillDown.kuerzel}"</h4>
                            {(eaDrillDown.egList || []).length === 0 ? (
                              <div style={{ color: darkMode ? '#9ca3af' : '#6b7280', fontSize: 13 }}>Keine Einflussgrößen in dieser Zeile.</div>
                            ) : (
                              <>
                                <label style={{ marginRight: 8, color: darkMode ? '#d1d5db' : '#374151' }}>Einflussgröße:</label>
                                <select
                                  value={eaDrillDown.selectedEG || ''}
                                  onChange={e => setEaDrillDown(prev => prev ? { ...prev, selectedEG: e.target.value } : null)}
                                  style={{ marginBottom: 12, padding: '4px 8px' }}
                                >
                                  {(() => {
                                    const mEA = (groupData[eaDrillDown.key]?.measurements || []);
                                    const { zeitbestimmend } = analyzeEinflussgroessenZeitbestimmend(mEA);
                                    return (eaDrillDown.egList || []).map(eg => (
                                      <option key={eg} value={eg}>{eg}{zeitbestimmend.includes(eg) ? '' : ' (ohne Relevanz)'}</option>
                                    ));
                                  })()}
                                </select>
                                {eaDrillDown.selectedEG && (() => {
                                  const eg = eaDrillDown.selectedEG;
                                  const measurements = (groupData[eaDrillDown.key]?.measurements || []).filter(m => m.einflussgroessen?.[eg] != null && m.einflussgroessen[eg] !== '');
                                  const categories = [...new Set(measurements.map(m => String(m.einflussgroessen[eg])))].sort();
                                  const points = measurements.map(m => ({ x: categories.indexOf(String(m.einflussgroessen[eg])) + 1, y: Number(m.vorgabeTime) || 0 }));
                                  const pointMeta = measurements.map(m => ({ number: m.number, activity: m.isNote ? `📝 ${m.note || ''}` : (m.activity || '-') }));
                                  const regression = categories.length >= 2 && points.length >= 3 ? simpleOLSWithBand(points.map(p => p.x), points.map(p => p.y)) : null;
                                  return (
                                    <EgRegressionChart
                                      points={points}
                                      pointMeta={pointMeta}
                                      categories={categories}
                                      regression={regression}
                                      formatTime={formatTime}
                                      darkMode={darkMode}
                                      width={480}
                                      height={280}
                                    />
                                  );
                                })()}
                              </>
                            )}
                          </div>
                        )}

                        {/* Einflussgrößen pro Kürzel+E / Kürzel+EA (nur Anzeige, keine Einzelanalyse – die gibt es nur bei Gruppierung Kürzel) */}
                        {((statistikGroupBy === 'kuerzelE' && Object.keys(analysis.byKuerzelE || {}).some(k => k.includes(' | '))) || (statistikGroupBy === 'kuerzelEA' && Object.keys(analysis.byKuerzelEA || {}).some(k => k.includes(' | ')))) && (
                          <div style={{ marginTop: '24px', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: darkMode ? '#1f2937' : '#f9fafb' }}>
                            <h4 style={{ marginTop: 0, marginBottom: '12px', color: darkMode ? '#f9fafb' : '#111827' }}>Einflussgrößen pro {statistikGroupBy === 'kuerzelEA' ? 'Kürzel+EA' : 'Kürzel+E'}</h4>
                            <div className="analysis-table-wrapper" style={{ overflowX: 'auto' }}>
                              <table className="analysis-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>
                                    <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{statistikGroupBy === 'kuerzelEA' ? 'Kürzel + EA' : 'Kürzel + E'}</th>
                                    <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Verwendete Einflussgrößen</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.keys(statistikGroupBy === 'kuerzelEA' ? (analysis.byKuerzelEA || {}) : (analysis.byKuerzelE || {})).sort((a,b)=>a.localeCompare(b)).map(key => {
                                    const g = (statistikGroupBy === 'kuerzelEA' ? analysis.byKuerzelEA : analysis.byKuerzelE)[key];
                                    const first = (g.measurements || [])[0];
                                    const egKeys = first && first.einflussgroessen && typeof first.einflussgroessen === 'object' ? Object.keys(first.einflussgroessen) : [];
                                    return (
                                      <tr key={key}>
                                        <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', color: darkMode ? '#e5e7eb' : '#111827' }} title={key}>{key.length > 50 ? key.slice(0, 47) + '…' : key}</td>
                                        <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', color: darkMode ? '#e5e7eb' : '#111827' }}>{egKeys.join(', ') || '–'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Zeitbestimmend-Analyse und Multivariate Regression: Sollzeit ~ Einflussgrößen */}
                        {(() => {
                          const srcEA = statistikGroupBy === 'kuerzelEA' ? (analysis.byKuerzelEA || {}) : (statistikGroupBy === 'kuerzelE' ? (analysis.byKuerzelE || {}) : (analysis.byKuerzelEA || {}));
                          const measurementsEA = Object.values(srcEA).flatMap(gg => gg.measurements || []);
                          if (measurementsEA.length < 2) return null;
                          const { allEG, zeitbestimmend, r2PerEG } = analyzeEinflussgroessenZeitbestimmend(measurementsEA);
                          const { X, y, colNames } = buildDesignMatrixEA(measurementsEA, zeitbestimmend);
                          const res = X.length >= X[0].length ? multilinearOLS(X, y) : { beta: colNames.map(() => 0), r2: 0 };
                          const gData = statistikGroupBy === 'kuerzel' ? (analysis.byKuerzel || {}) : (statistikGroupBy === 'kuerzelE' ? (analysis.byKuerzelE || {}) : (analysis.byKuerzelEA || {}));
                          const gKeys = Object.keys(gData);
                          const points = gKeys.map(kk => { const g = gData[kk]; return [g.mean || 0, g.count || 0, parseFloat(g.avgGrade) || 0]; });
                          let clusterLabels = [];
                          if (points.length >= 3) {
                            const kVal = Math.min(3, points.length);
                            const assign = kMeans(points, kVal, 25);
                            clusterLabels = gKeys.map((key, i) => ({ key, c: assign[i] }));
                          }
                          return (
                            <div style={{ marginTop: '24px', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: darkMode ? '#1f2937' : '#f9fafb' }}>
                              <h4 style={{ marginTop: 0, marginBottom: '12px', color: darkMode ? '#f9fafb' : '#111827' }}>Zeitbestimmende Einflussgrößen (Sollzeit)</h4>
                              {allEG.length === 0 ? (
                                <div style={{ fontSize: '13px', color: darkMode ? '#d1d5db' : '#6b7280' }}>Keine Einflussgrößen in den Daten.</div>
                              ) : (
                                <>
                                  <div style={{ fontSize: '12px', marginBottom: '8px', color: darkMode ? '#d1d5db' : '#374151' }}>
                                    <strong>R² je Einflussgröße (Sollzeit ~ Ausprägung):</strong>{' '}
                                    {allEG.sort((a,b)=>(r2PerEG[b]||0)-(r2PerEG[a]||0)).map(eg => `${eg}: ${(r2PerEG[eg]||0).toFixed(3)}`).join('; ')}
                                  </div>
                                  <div style={{ fontSize: '12px', marginBottom: '12px', color: darkMode ? '#9ca3af' : '#6b7280' }}>
                                    <strong>In weitere Analyse einbezogen (zeitbestimmend, R²≥1%):</strong> {zeitbestimmend.length ? zeitbestimmend.join(', ') : '–'}
                                  </div>
                                  {zeitbestimmend.length > 0 && res.beta && (
                                    <div style={{ fontSize: '13px', marginBottom: '12px' }}>
                                      <strong>Multivariate Regression Sollzeit ~ Einflussgrößen:</strong>{' '}
                                      y = {res.beta.map((b, i) => i === 0 ? b.toFixed(1) : (b >= 0 ? '+ ' : '- ') + Math.abs(b).toFixed(2) + '\u00B7' + (colNames[i] || '?')).join(' ')} &emsp; R² = {res.r2.toFixed(3)}
                                    </div>
                                  )}
                                </>
                              )}
                              {clusterLabels.length > 0 && (
                                <>
                                  <h4 style={{ marginTop: '16px', marginBottom: '8px', color: darkMode ? '#f9fafb' : '#111827' }}>Clustering (K-Means, k=3, Merkmale: Ø-Zeit, Anzahl, Ø-Leistungsgrad)</h4>
                                  <div style={{ fontSize: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {[0, 1, 2].map(c => (
                                      <div key={c} style={{ flex: '1 1 200px' }}>
                                        <strong>Cluster {c + 1}:</strong> {clusterLabels.filter(x => x.c === c).map(x => x.key).join(', ') || '–'}
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    );
                  })()}

                  {/* Bilanz Zeitarten */}
                  {analysisViewMode === 'timeType' && (() => {
                    const timeTypes = Object.keys(analysis.byTimeType || {}).sort((a, b) => {
                      if (a === 'Keine Zeitart') return 1;
                      if (b === 'Keine Zeitart') return -1;
                      return a.localeCompare(b);
                    });
                    let grandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };

                    timeTypes.forEach(timeType => {
                      const data = analysis.byTimeType[timeType];
                      grandTotal.count += data.count;
                      grandTotal.totalTime += data.totalTime;
                      grandTotal.totalVorgabeTime += (data.totalVorgabeTime || 0);
                      grandTotal.grades.push(...data.grades);
                    });

                    // Hole Beschreibung für Zeitarten
                    const getTimeTypeDescription = (timeTypeName) => {
                      const timeTypeObj = defaultTimeTypes.find(tt => tt.name === timeTypeName);
                      return timeTypeObj ? timeTypeObj.description : '';
                    };

                    // Sammle x__ Zeitarten
                    const excludedTimeTypes = Object.keys(analysis.excludedTimeTypes || {}).sort();
                    let excludedGrandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
                    excludedTimeTypes.forEach(timeType => {
                      const data = analysis.excludedTimeTypes[timeType];
                      excludedGrandTotal.count += data.count;
                      excludedGrandTotal.totalTime += data.totalTime;
                      excludedGrandTotal.totalVorgabeTime += (data.totalVorgabeTime || 0);
                      excludedGrandTotal.grades.push(...data.grades);
                    });
                    return (
                      <>
                        {renderChartTypeToggle(bilanzZeitartenChartType, setBilanzZeitartenChartType, 'bilanz-zeitarten', bilanzZeitartenTimeMode, setBilanzZeitartenTimeMode)}
                        {bilanzZeitartenChartType === 'balken' && (() => {
                          const fmtSoll = (v) => formatSollzeit(v, decimalPlacesSollzeiten) + ' min';
                          const sortKey = (k) => {
                            const d = analysis.byTimeType[k] || {};
                            if (bilanzZeitartenTimeMode === 'soll') return d.totalVorgabeTime || 0;
                            return d.totalTime || 0;
                          };
                          const sorted = [...timeTypes].sort((a, b) => sortKey(b) - sortKey(a));
                          const maxVal = sorted.reduce((m, k) => {
                            const d = analysis.byTimeType[k] || {};
                            return Math.max(m, d.totalTime || 0, d.totalVorgabeTime || 0);
                          }, 0);
                          if (maxVal <= 0) return <div style={{ marginTop: '12px', fontSize: '13px', color: darkMode ? '#9ca3af' : '#6b7280' }}>Keine Daten fuer eine Darstellung.</div>;
                          return (
                            <div style={{ marginTop: '12px' }}>
                              {sorted.map(name => {
                                const data = analysis.byTimeType[name];
                                const ist = data?.totalTime || 0;
                                const soll = data?.totalVorgabeTime || 0;
                                const color = colorForTimeType(name);
                                const desc = getTimeTypeDescription(name);
                                const clickable = (data?.measurements?.length || 0) > 0;
                                const pctIst = maxVal > 0 ? (ist / maxVal) * 100 : 0;
                                const pctSoll = maxVal > 0 ? (soll / maxVal) * 100 : 0;
                                const showIst = bilanzZeitartenTimeMode !== 'soll';
                                const showSoll = bilanzZeitartenTimeMode !== 'ist';
                                const headerLine = bilanzZeitartenTimeMode === 'vergleich'
                                  ? `IST ${formatTime(ist)} / SOLL ${fmtSoll(soll)} (${data?.count || 0})`
                                  : (bilanzZeitartenTimeMode === 'soll' ? `${fmtSoll(soll)} (${data?.count || 0})` : `${formatTime(ist)} (${data?.count || 0})`);
                                return (
                                  <div
                                    key={name}
                                    style={{ marginBottom: '10px', cursor: clickable ? 'pointer' : 'default' }}
                                    onClick={() => { if (clickable) setPivotDrillDown({ timeType: name, measurements: data.measurements }); }}
                                    title={clickable ? `Klicken fuer Details: ${data.count} Messpunkt(e)` : ''}
                                  >
                                    <div style={{ fontSize: '12px', marginBottom: '2px', color: darkMode ? '#d1d5db' : '#374151' }}>
                                      <strong>{name}</strong>{desc ? ` - ${desc}` : ''} - {headerLine}
                                    </div>
                                    {showIst && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: showSoll && bilanzZeitartenTimeMode === 'vergleich' ? 3 : 0 }}>
                                        {bilanzZeitartenTimeMode === 'vergleich' && <span style={{ fontSize: 10, fontWeight: 700, color: darkMode ? '#e5e7eb' : '#111827', minWidth: 32, textAlign: 'right' }}>IST</span>}
                                        <div style={{ flex: 1, height: '18px', background: darkMode ? '#374151' : '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                                          <div style={{ height: '100%', width: pctIst + '%', background: color, borderRadius: '4px', minWidth: ist > 0 ? '4px' : 0 }} />
                                        </div>
                                      </div>
                                    )}
                                    {showSoll && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {bilanzZeitartenTimeMode === 'vergleich' && <span style={{ fontSize: 10, fontWeight: 700, color: darkMode ? '#9ca3af' : '#6b7280', minWidth: 32, textAlign: 'right' }}>SOLL</span>}
                                        <div style={{ flex: 1, height: '18px', background: darkMode ? '#374151' : '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                                          <div style={{
                                            height: '100%',
                                            width: pctSoll + '%',
                                            background: bilanzZeitartenTimeMode === 'vergleich' ? 'transparent' : color,
                                            border: bilanzZeitartenTimeMode === 'vergleich' ? `2px dashed ${color}` : 'none',
                                            boxSizing: 'border-box',
                                            borderRadius: '4px',
                                            minWidth: soll > 0 ? '4px' : 0
                                          }} />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                        {bilanzZeitartenChartType === 'kreis' && (() => {
                          const fmtSoll = (v) => formatSollzeit(v, decimalPlacesSollzeiten) + ' min';
                          const buildSlices = (mode) => timeTypes.map(name => {
                            const data = analysis.byTimeType[name] || {};
                            const value = mode === 'soll' ? (data.totalVorgabeTime || 0) : (data.totalTime || 0);
                            return { key: name, label: name, value, count: data.count || 0, color: colorForTimeType(name) };
                          });
                          const onSliceClick = (key) => {
                            const data = analysis.byTimeType[key];
                            if (data && data.measurements && data.measurements.length > 0) {
                              setPivotDrillDown({ timeType: key, measurements: data.measurements });
                            }
                          };
                          if (bilanzZeitartenTimeMode === 'vergleich') {
                            return (
                              <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                                <div style={{ flex: '1 1 320px', minWidth: 280 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: darkMode ? '#e5e7eb' : '#111827' }}>IST</div>
                                  <AnalysisPieChart
                                    slices={buildSlices('ist')}
                                    formatValue={formatTime}
                                    centerValue={formatTime(grandTotal.totalTime)}
                                    centerLabel="Summe IST"
                                    darkMode={darkMode}
                                    size={220}
                                    ariaLabel="Bilanz Zeitarten Kreisdiagramm IST"
                                    onSliceClick={onSliceClick}
                                  />
                                </div>
                                <div style={{ flex: '1 1 320px', minWidth: 280 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: darkMode ? '#e5e7eb' : '#111827' }}>SOLL</div>
                                  <AnalysisPieChart
                                    slices={buildSlices('soll')}
                                    formatValue={fmtSoll}
                                    centerValue={fmtSoll(grandTotal.totalVorgabeTime || 0)}
                                    centerLabel="Summe SOLL"
                                    darkMode={darkMode}
                                    size={220}
                                    ariaLabel="Bilanz Zeitarten Kreisdiagramm SOLL"
                                    onSliceClick={onSliceClick}
                                  />
                                </div>
                              </div>
                            );
                          }
                          const isSoll = bilanzZeitartenTimeMode === 'soll';
                          return (
                            <div style={{ marginTop: '12px' }}>
                              <AnalysisPieChart
                                slices={buildSlices(isSoll ? 'soll' : 'ist')}
                                formatValue={isSoll ? fmtSoll : formatTime}
                                centerValue={isSoll ? fmtSoll(grandTotal.totalVorgabeTime || 0) : formatTime(grandTotal.totalTime)}
                                centerLabel={isSoll ? 'Summe SOLL' : 'Summe IST'}
                                darkMode={darkMode}
                                size={240}
                                ariaLabel="Bilanz Zeitarten Kreisdiagramm"
                                onSliceClick={onSliceClick}
                              />
                            </div>
                          );
                        })()}
                        {bilanzZeitartenChartType === 'tabelle' && (
                        <div className="analysis-table-wrapper" style={{ marginTop: '20px' }}>
                          <table className="analysis-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                              <tr style={{ backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Zeitart</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Beschreibung</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Anzahl</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Gesamtzeit (IST)</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Vorgabezeit (SOLL)</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Ø Leistungsgrad</th>
                              </tr>
                            </thead>
                            <tbody>
                              {timeTypes.map(timeType => {
                                const data = analysis.byTimeType[timeType];
                                const description = getTimeTypeDescription(timeType);
                                return (
                                  <tr 
                                    key={timeType}
                                    style={{ cursor: data.measurements && data.measurements.length > 0 ? 'pointer' : 'default' }}
                                    onClick={() => {
                                      if (data.measurements && data.measurements.length > 0) {
                                        setPivotDrillDown({ timeType, measurements: data.measurements });
                                      }
                                    }}
                                    title={data.measurements && data.measurements.length > 0 ? `Klicken für Details: ${data.count} Messpunkt(e)` : ''}
                                    onMouseEnter={(e) => {
                                      if (data.measurements && data.measurements.length > 0) {
                                        e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                  >
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', fontWeight: 'bold', color: darkMode ? '#e5e7eb' : '#111827' }}>{timeType}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>{description || '-'}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{data.count}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(data.totalTime)}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatSollzeit(data.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatGrade(data.avgGrade)}</td>
                                  </tr>
                                );
                              })}
                              <tr style={{ backgroundColor: darkMode ? '#1f2937' : '#e5e7eb', fontWeight: 'bold' }}>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>Gesamt</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}></td>
                                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{grandTotal.count}</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{formatTime(grandTotal.totalTime)}</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{formatSollzeit(grandTotal.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        )}
                        
                        {/* x__ Zeitarten am Ende */}
                        {excludedTimeTypes.length > 0 && (
                          <div className="analysis-table-wrapper" style={{ marginTop: '30px' }}>
                            <h4 style={{ marginBottom: '10px', color: darkMode ? '#f9fafb' : '#111827' }}>Nicht zu verwendende Zeit (x__)</h4>
                            <table className="analysis-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                              <thead>
                                <tr style={{ backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Zeitart</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Beschreibung</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Anzahl</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Gesamtzeit (IST)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {excludedTimeTypes.map(timeType => {
                                  const data = analysis.excludedTimeTypes[timeType];
                                  const description = getTimeTypeDescription(timeType);
                                  return (
                                    <tr 
                                      key={timeType}
                                      style={{ cursor: data.measurements && data.measurements.length > 0 ? 'pointer' : 'default' }}
                                      onClick={() => {
                                        if (data.measurements && data.measurements.length > 0) {
                                          setPivotDrillDown({ timeType, measurements: data.measurements });
                                        }
                                      }}
                                      title={data.measurements && data.measurements.length > 0 ? `Klicken für Details: ${data.count} Messpunkt(e)` : ''}
                                      onMouseEnter={(e) => {
                                        if (data.measurements && data.measurements.length > 0) {
                                          e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                      }}
                                    >
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', fontWeight: 'bold', color: darkMode ? '#e5e7eb' : '#111827' }}>{timeType}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>{description || '-'}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{data.count}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(data.totalTime)}</td>
                                    </tr>
                                  );
                                })}
                                <tr style={{ backgroundColor: darkMode ? '#1f2937' : '#e5e7eb', fontWeight: 'bold' }}>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>Gesamt</td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}></td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{excludedGrandTotal.count}</td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(excludedGrandTotal.totalTime)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* Bilanz Kürzel */}
                  {analysisViewMode === 'kuerzel' && (() => {
                    const kuerzels = Object.keys(analysis.byKuerzel || {}).sort((a, b) => {
                      if (a === 'Kein Kürzel') return 1;
                      if (b === 'Kein Kürzel') return -1;
                      return a.localeCompare(b);
                    });
                    let grandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };

                    kuerzels.forEach(kuerzel => {
                      const data = analysis.byKuerzel[kuerzel];
                      grandTotal.count += data.count;
                      grandTotal.totalTime += data.totalTime;
                      grandTotal.totalVorgabeTime += (data.totalVorgabeTime || 0);
                      grandTotal.grades.push(...data.grades);
                    });

                    // Hole Beschreibung für Kürzel
                    const getKuerzelDescription = (kuerzelName) => {
                      if (kuerzelName === 'Kein Kürzel') return '';
                      const kuerzelObj = ablaufDbData.find(k => k.kuerzel === kuerzelName);
                      return kuerzelObj ? (kuerzelObj.beschreibung || kuerzelObj.fullText || '') : '';
                    };

                    return (
                      <>
                        {renderChartTypeToggle(bilanzKuerzelChartType, setBilanzKuerzelChartType, 'bilanz-kuerzel', bilanzKuerzelTimeMode, setBilanzKuerzelTimeMode)}
                        {bilanzKuerzelChartType === 'balken' && (() => {
                          const fmtSoll = (v) => formatSollzeit(v, decimalPlacesSollzeiten) + ' min';
                          const sortKey = (k) => {
                            const d = analysis.byKuerzel[k] || {};
                            if (bilanzKuerzelTimeMode === 'soll') return d.totalVorgabeTime || 0;
                            return d.totalTime || 0;
                          };
                          const sorted = [...kuerzels].sort((a, b) => sortKey(b) - sortKey(a));
                          const maxVal = sorted.reduce((m, k) => {
                            const d = analysis.byKuerzel[k] || {};
                            return Math.max(m, d.totalTime || 0, d.totalVorgabeTime || 0);
                          }, 0);
                          if (maxVal <= 0) return <div style={{ marginTop: '12px', fontSize: '13px', color: darkMode ? '#9ca3af' : '#6b7280' }}>Keine Daten fuer eine Darstellung.</div>;
                          return (
                            <div style={{ marginTop: '12px' }}>
                              {sorted.map(name => {
                                const data = analysis.byKuerzel[name];
                                const ist = data?.totalTime || 0;
                                const soll = data?.totalVorgabeTime || 0;
                                const color = colorForKuerzel(name);
                                const desc = getKuerzelDescription(name);
                                const clickable = (data?.measurements?.length || 0) > 0;
                                const pctIst = maxVal > 0 ? (ist / maxVal) * 100 : 0;
                                const pctSoll = maxVal > 0 ? (soll / maxVal) * 100 : 0;
                                const showIst = bilanzKuerzelTimeMode !== 'soll';
                                const showSoll = bilanzKuerzelTimeMode !== 'ist';
                                const headerLine = bilanzKuerzelTimeMode === 'vergleich'
                                  ? `IST ${formatTime(ist)} / SOLL ${fmtSoll(soll)} (${data?.count || 0})`
                                  : (bilanzKuerzelTimeMode === 'soll' ? `${fmtSoll(soll)} (${data?.count || 0})` : `${formatTime(ist)} (${data?.count || 0})`);
                                return (
                                  <div
                                    key={name}
                                    style={{ marginBottom: '10px', cursor: clickable ? 'pointer' : 'default' }}
                                    onClick={() => { if (clickable) setPivotDrillDown({ kuerzel: name, measurements: data.measurements }); }}
                                    title={clickable ? `Klicken fuer Details: ${data.count} Messpunkt(e)` : ''}
                                  >
                                    <div style={{ fontSize: '12px', marginBottom: '2px', color: darkMode ? '#d1d5db' : '#374151' }}>
                                      <strong>{name}</strong>{desc ? ` - ${desc}` : ''} - {headerLine}
                                    </div>
                                    {showIst && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: showSoll && bilanzKuerzelTimeMode === 'vergleich' ? 3 : 0 }}>
                                        {bilanzKuerzelTimeMode === 'vergleich' && <span style={{ fontSize: 10, fontWeight: 700, color: darkMode ? '#e5e7eb' : '#111827', minWidth: 32, textAlign: 'right' }}>IST</span>}
                                        <div style={{ flex: 1, height: '18px', background: darkMode ? '#374151' : '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                                          <div style={{ height: '100%', width: pctIst + '%', background: color, borderRadius: '4px', minWidth: ist > 0 ? '4px' : 0 }} />
                                        </div>
                                      </div>
                                    )}
                                    {showSoll && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {bilanzKuerzelTimeMode === 'vergleich' && <span style={{ fontSize: 10, fontWeight: 700, color: darkMode ? '#9ca3af' : '#6b7280', minWidth: 32, textAlign: 'right' }}>SOLL</span>}
                                        <div style={{ flex: 1, height: '18px', background: darkMode ? '#374151' : '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                                          <div style={{
                                            height: '100%',
                                            width: pctSoll + '%',
                                            background: bilanzKuerzelTimeMode === 'vergleich' ? 'transparent' : color,
                                            border: bilanzKuerzelTimeMode === 'vergleich' ? `2px dashed ${color}` : 'none',
                                            boxSizing: 'border-box',
                                            borderRadius: '4px',
                                            minWidth: soll > 0 ? '4px' : 0
                                          }} />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                        {bilanzKuerzelChartType === 'kreis' && (() => {
                          const fmtSoll = (v) => formatSollzeit(v, decimalPlacesSollzeiten) + ' min';
                          const buildSlices = (mode) => kuerzels.map(name => {
                            const data = analysis.byKuerzel[name] || {};
                            const value = mode === 'soll' ? (data.totalVorgabeTime || 0) : (data.totalTime || 0);
                            return { key: name, label: name, value, count: data.count || 0, color: colorForKuerzel(name) };
                          });
                          const onSliceClick = (key) => {
                            const data = analysis.byKuerzel[key];
                            if (data && data.measurements && data.measurements.length > 0) {
                              setPivotDrillDown({ kuerzel: key, measurements: data.measurements });
                            }
                          };
                          if (bilanzKuerzelTimeMode === 'vergleich') {
                            return (
                              <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                                <div style={{ flex: '1 1 320px', minWidth: 280 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: darkMode ? '#e5e7eb' : '#111827' }}>IST</div>
                                  <AnalysisPieChart
                                    slices={buildSlices('ist')}
                                    formatValue={formatTime}
                                    centerValue={formatTime(grandTotal.totalTime)}
                                    centerLabel="Summe IST"
                                    darkMode={darkMode}
                                    size={220}
                                    ariaLabel="Bilanz Kuerzel Kreisdiagramm IST"
                                    onSliceClick={onSliceClick}
                                  />
                                </div>
                                <div style={{ flex: '1 1 320px', minWidth: 280 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: darkMode ? '#e5e7eb' : '#111827' }}>SOLL</div>
                                  <AnalysisPieChart
                                    slices={buildSlices('soll')}
                                    formatValue={fmtSoll}
                                    centerValue={fmtSoll(grandTotal.totalVorgabeTime || 0)}
                                    centerLabel="Summe SOLL"
                                    darkMode={darkMode}
                                    size={220}
                                    ariaLabel="Bilanz Kuerzel Kreisdiagramm SOLL"
                                    onSliceClick={onSliceClick}
                                  />
                                </div>
                              </div>
                            );
                          }
                          const isSoll = bilanzKuerzelTimeMode === 'soll';
                          return (
                            <div style={{ marginTop: '12px' }}>
                              <AnalysisPieChart
                                slices={buildSlices(isSoll ? 'soll' : 'ist')}
                                formatValue={isSoll ? fmtSoll : formatTime}
                                centerValue={isSoll ? fmtSoll(grandTotal.totalVorgabeTime || 0) : formatTime(grandTotal.totalTime)}
                                centerLabel={isSoll ? 'Summe SOLL' : 'Summe IST'}
                                darkMode={darkMode}
                                size={240}
                                ariaLabel="Bilanz Kuerzel Kreisdiagramm"
                                onSliceClick={onSliceClick}
                              />
                            </div>
                          );
                        })()}
                        {bilanzKuerzelChartType === 'tabelle' && (
                        <div className="analysis-table-wrapper" style={{ marginTop: '20px' }}>
                          <table className="analysis-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                              <tr style={{ backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Kürzel</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Tätigkeit</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Anzahl</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Gesamtzeit (IST)</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Vorgabezeit (SOLL)</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Ø Leistungsgrad</th>
                              </tr>
                            </thead>
                            <tbody>
                              {kuerzels.map(kuerzel => {
                                const data = analysis.byKuerzel[kuerzel];
                                const description = getKuerzelDescription(kuerzel);
                                return (
                                  <tr 
                                    key={kuerzel}
                                    style={{ cursor: data.measurements && data.measurements.length > 0 ? 'pointer' : 'default' }}
                                    onClick={() => {
                                      if (data.measurements && data.measurements.length > 0) {
                                        setPivotDrillDown({ kuerzel, measurements: data.measurements });
                                      }
                                    }}
                                    title={data.measurements && data.measurements.length > 0 ? `Klicken für Details: ${data.count} Messpunkt(e)` : ''}
                                    onMouseEnter={(e) => {
                                      if (data.measurements && data.measurements.length > 0) {
                                        e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                  >
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', fontWeight: 'bold', color: darkMode ? '#e5e7eb' : '#111827' }}>{kuerzel}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>{description || '-'}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{data.count}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(data.totalTime)}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatSollzeit(data.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatGrade(data.avgGrade)}</td>
                                  </tr>
                                );
                              })}
                              <tr style={{ backgroundColor: darkMode ? '#1f2937' : '#e5e7eb', fontWeight: 'bold' }}>
                                <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>Gesamt</td>
                                <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}></td>
                                <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{grandTotal.count}</td>
                                <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(grandTotal.totalTime)}</td>
                                <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatSollzeit(grandTotal.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</td>
                                <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        )}
                        
                        {/* Kürzel mit x__ Zeitarten am Ende */}
                        {Object.keys(analysis.excludedKuerzels || {}).length > 0 && (() => {
                        const excludedKuerzels = Object.keys(analysis.excludedKuerzels || {}).sort((a, b) => {
                          if (a === 'Kein Kürzel') return 1;
                          if (b === 'Kein Kürzel') return -1;
                          return a.localeCompare(b);
                        });
                        let excludedGrandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
                        excludedKuerzels.forEach(kuerzel => {
                          const data = analysis.excludedKuerzels[kuerzel];
                          excludedGrandTotal.count += data.count;
                          excludedGrandTotal.totalTime += data.totalTime;
                          excludedGrandTotal.totalVorgabeTime += (data.totalVorgabeTime || 0);
                          excludedGrandTotal.grades.push(...data.grades);
                        });
                        return (
                          <div className="analysis-table-wrapper" style={{ marginTop: '30px' }}>
                            <h4 style={{ marginBottom: '10px', color: darkMode ? '#f9fafb' : '#111827' }}>Kürzel mit nicht zu verwendender Zeit (x__)</h4>
                            <table className="analysis-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                              <thead>
                                <tr style={{ backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Kürzel</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Tätigkeit</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Anzahl</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Gesamtzeit (IST)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {excludedKuerzels.map(kuerzel => {
                                  const data = analysis.excludedKuerzels[kuerzel];
                                  const description = getKuerzelDescription(kuerzel);
                                  return (
                                    <tr 
                                      key={kuerzel}
                                      style={{ cursor: data.measurements && data.measurements.length > 0 ? 'pointer' : 'default' }}
                                      onClick={() => {
                                        if (data.measurements && data.measurements.length > 0) {
                                          setPivotDrillDown({ kuerzel, measurements: data.measurements });
                                        }
                                      }}
                                      title={data.measurements && data.measurements.length > 0 ? `Klicken für Details: ${data.count} Messpunkt(e)` : ''}
                                      onMouseEnter={(e) => {
                                        if (data.measurements && data.measurements.length > 0) {
                                          e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                      }}
                                    >
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', fontWeight: 'bold', color: darkMode ? '#e5e7eb' : '#111827' }}>{kuerzel}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>{description || '-'}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{data.count}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(data.totalTime)}</td>
                                    </tr>
                                  );
                                })}
                                <tr style={{ backgroundColor: darkMode ? '#1f2937' : '#e5e7eb', fontWeight: 'bold' }}>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>Gesamt</td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}></td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{excludedGrandTotal.count}</td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(excludedGrandTotal.totalTime)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                      </>
                    );
                  })()}

                  {/* Zeitstrahl: Drill-down pro WA-Nr (zugeklappt), aufgeklappt: Tätigkeiten mit positioniertem Balken */}
                  {analysisViewMode === 'zeitstrahl' && (() => {
                    const flatMeasurements = Object.values(analysis.byTimeType || {}).flatMap(d => d.measurements || []);
                    const byWaNr = {};
                    flatMeasurements.forEach(m => {
                      const key = m.resumeNumber ?? '—';
                      if (!byWaNr[key]) byWaNr[key] = [];
                      byWaNr[key].push(m);
                    });
                    const orderKeys = Object.keys(byWaNr).sort((a, b) => {
                      if (a === '—') return 1;
                      if (b === '—') return -1;
                      const na = Number(a);
                      const nb = Number(b);
                      if (!isNaN(na) && !isNaN(nb)) return na - nb;
                      return String(a).localeCompare(String(b));
                    });
                    const fallbackColor = '#9ca3af';
                    const getColor = (m) => {
                      const key = (m.timeType == null || m.timeType === '') ? 'Keine Zeitart' : m.timeType;
                      const fromConfig = (timeTypes.find(t => t.name === key) || {}).color;
                      const fromDefaults = (defaultTimeTypes.find(d => d.name === key) || {}).color;
                      return fromConfig || fromDefaults || (key === 'Keine Zeitart' ? '#94a3b8' : null) || fallbackColor;
                    };
                    const toT = (x) => {
                      const v = x?.timestamp ? new Date(x.timestamp).getTime() : 0;
                      return (typeof v === 'number' && !isNaN(v)) ? v : 0;
                    };
                    const toggleWa = (waKey) => {
                      setZeitstrahlExpandedWaNr(prev => {
                        const n = new Set(prev);
                        if (n.has(waKey)) n.delete(waKey); else n.add(waKey);
                        return n;
                      });
                    };
                    const onSegmentEnter = (e, m) => { clearTimeout(zeitstrahlHoverTimeoutRef.current); setZeitstrahlHover({ m, rect: e.currentTarget.getBoundingClientRect() }); };
                    const onSegmentLeave = () => { zeitstrahlHoverTimeoutRef.current = setTimeout(() => setZeitstrahlHover(null), 180); };
                    return (
                      <div className="compact-zeitstrahl" style={{ marginTop: 16 }}>
                        {orderKeys.map(waKey => {
                          const list = [...(byWaNr[waKey] || [])].sort((a, b) => toT(a) - toT(b));
                          return (
                            <ZeitstrahlWaBlock
                              key={waKey}
                              waKey={waKey}
                              list={list}
                              expanded={zeitstrahlExpandedWaNr.has(waKey)}
                              onToggle={toggleWa}
                              getColor={getColor}
                              formatTime={formatTime}
                              onSegmentEnter={onSegmentEnter}
                              onSegmentLeave={onSegmentLeave}
                            />
                          );
                        })}
                        {zeitstrahlHover && (() => {
                          const { m, rect } = zeitstrahlHover;
                          const descZeitart = (timeTypes.find(t => t.name === m.timeType) || defaultTimeTypes.find(d => d.name === m.timeType))?.description;
                          const kuerzelMatch = (ablaufDbData && Array.isArray(ablaufDbData)) ? ablaufDbData.find(k => k.kuerzel === m.kuerzel) : null;
                          const descKuerzel = kuerzelMatch ? (kuerzelMatch.beschreibung || kuerzelMatch.fullText || '') : '';
                          const eaArr = m.einflussgroessen && typeof m.einflussgroessen === 'object' && !Array.isArray(m.einflussgroessen)
                            ? Object.entries(m.einflussgroessen).map(([k, v]) => `${k}: ${v != null ? String(v) : ''}`)
                            : [];
                          const photos = (measurementPhotos[m.number] || []);
                          let left = rect.left;
                          let top = rect.bottom + 6;
                          const w = 320;
                          if (left + w > window.innerWidth) left = window.innerWidth - w - 8;
                          if (left < 8) left = 8;
                          if (top + 340 > window.innerHeight - 8 && rect.top > 360) { top = rect.top - 344; }
                          return (
                            <div
                              className={`zeitstrahl-popover ${darkMode ? 'zeitstrahl-popover-dark' : ''}`}
                              style={{ position: 'fixed', left, top, maxWidth: w, maxHeight: 340, zIndex: 10000 }}
                              onMouseEnter={() => clearTimeout(zeitstrahlHoverTimeoutRef.current)}
                              onMouseLeave={() => setZeitstrahlHover(null)}
                            >
                              <div className="zeitstrahl-popover-header">
                                #{m.number} {m.kuerzel || m.activity || '—'} · {formatTime(m.individualTime || 0)} · {m.timeType || 'Keine Zeitart'}
                              </div>
                              {descZeitart && <div className="zeitstrahl-popover-line"><strong>Zeitart:</strong> {descZeitart}</div>}
                              {descKuerzel && <div className="zeitstrahl-popover-line"><strong>Tätigkeit:</strong> {descKuerzel}</div>}
                              {m.note && <div className="zeitstrahl-popover-line"><strong>Notiz:</strong> {m.note}</div>}
                              {eaArr.length > 0 && <div className="zeitstrahl-popover-line"><strong>Einflussgrößen:</strong> {eaArr.join(', ')}</div>}
                              {photos.length > 0 && (
                                <div className="zeitstrahl-popover-photos">
                                  {photos.map((p) => (
                                    <img
                                      key={p.id}
                                      src={p.fileData}
                                      alt={p.fileName}
                                      title={p.fileName}
                                      onClick={() => { setPhotoPreview(p.fileData); setZeitstrahlHover(null); }}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {orderKeys.length === 0 && (
                          <div className="compact-zeitstrahl-empty">Keine Messdaten (ohne x__) für den Zeitstrahl.</div>
                        )}
                        {orderKeys.length > 0 && (
                          <div className="compact-zeitstrahl-hint" style={{ marginTop: 8, fontSize: '0.85rem', color: darkMode ? '#9ca3af' : '#6b7280' }}>
                            💡 Klicken zum Auf-/Zuklappen; Hover für Beschreibung, Notiz, EA, Fotos
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Pivot-Tabelle */}
                  {analysisViewMode === 'pivot' && (() => {
                    const activities = Object.keys(analysis.pivot).sort();
                    // Filtere "x__" Zeitarten aus der Pivot-Tabelle
                    const timeTypes = [...new Set(Object.values(analysis.pivot).flatMap(activity => Object.keys(activity)))].filter(tt => tt !== 'x__').sort();
                    
                    // Berechne Gesamtzeilen und -spalten
                    const rowTotals = {};
                    const colTotals = {};
                    let grandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
                    
                    activities.forEach(activity => {
                      rowTotals[activity] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
                      Object.keys(analysis.pivot[activity]).filter(tt => tt !== 'x__').forEach(timeType => {
                        const cell = analysis.pivot[activity][timeType];
                        if (cell) {
                          rowTotals[activity].count += cell.count;
                          rowTotals[activity].totalTime += cell.totalTime;
                          rowTotals[activity].totalVorgabeTime += (cell.totalVorgabeTime || 0);
                          rowTotals[activity].grades.push(...cell.grades);
                          grandTotal.count += cell.count;
                          grandTotal.totalTime += cell.totalTime;
                          grandTotal.totalVorgabeTime += (cell.totalVorgabeTime || 0);
                          grandTotal.grades.push(...cell.grades);
                        }
                      });
                      rowTotals[activity].avgGrade = calculateAvgGrade(rowTotals[activity].grades);
                    });
                    
                    timeTypes.forEach(timeType => {
                      colTotals[timeType] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
                      activities.forEach(activity => {
                        if (analysis.pivot[activity][timeType]) {
                          const cell = analysis.pivot[activity][timeType];
                          colTotals[timeType].count += cell.count;
                          colTotals[timeType].totalTime += cell.totalTime;
                          colTotals[timeType].totalVorgabeTime += (cell.totalVorgabeTime || 0);
                          colTotals[timeType].grades.push(...cell.grades);
                        }
                      });
                      colTotals[timeType].avgGrade = calculateAvgGrade(colTotals[timeType].grades);
                    });
                    
                    return (
                <div className="pivot-container">
                  <div className="pivot-table-wrapper">
                    <table className="pivot-table">
                      <thead>
                        <tr>
                          <th className="pivot-corner">Tätigkeit / Zeitart</th>
                          {timeTypes.map(timeType => (
                            <th key={timeType} className="pivot-col-header">
                              <div className="pivot-header-content">
                                <span className="pivot-header-title">{timeType}</span>
                              </div>
                            </th>
                          ))}
                          <th className="pivot-row-total-header">Gesamt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activities.map(activity => (
                          <tr key={activity} className="pivot-row">
                            <td className="pivot-row-header">
                              <div className="pivot-row-header-content">
                                <span className="pivot-row-title">{activity}</span>
                              </div>
                            </td>
                            {timeTypes.map(timeType => {
                              const cell = analysis.pivot[activity][timeType];
                              return (
                                <td 
                                  key={timeType} 
                                  className={`pivot-cell ${cell && cell.count > 0 ? 'has-data' : 'no-data'}`}
                                  onClick={() => {
                                    if (cell && cell.count > 0) {
                                      setPivotDrillDown({ activity, timeType, measurements: cell.measurements });
                                    }
                                  }}
                                  title={cell && cell.count > 0 ? `Klicken für Details: ${cell.count} Messpunkt(e)` : 'Keine Daten'}
                                >
                                  {cell && cell.count > 0 ? (
                                    <div className="pivot-cell-content">
                                      <div className="pivot-cell-count">{cell.count}</div>
                                      <div className="pivot-cell-time">{formatTime(cell.totalTime)}</div>
                                      <div className="pivot-cell-time" style={{ fontSize: '0.85em', opacity: 0.8 }}>SOLL: {formatSollzeit(cell.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</div>
                                      {formatGrade(cell.avgGrade) && <div className="pivot-cell-grade">{formatGrade(cell.avgGrade)}</div>}
                                    </div>
                                  ) : (
                                    <div className="pivot-cell-empty">—</div>
                                  )}
                                </td>
                              );
                            })}
                            <td className="pivot-row-total">
                              <div className="pivot-cell-content">
                                <div className="pivot-cell-count">{rowTotals[activity].count}</div>
                                <div className="pivot-cell-time">{formatTime(rowTotals[activity].totalTime)}</div>
                                <div className="pivot-cell-time" style={{ fontSize: '0.85em', opacity: 0.8 }}>SOLL: {formatSollzeit(rowTotals[activity].totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</div>
                              </div>
                            </td>
                          </tr>
                        ))}
                        <tr className="pivot-total-row">
                          <td className="pivot-col-total-header">Gesamt</td>
                          {timeTypes.map(timeType => (
                            <td key={timeType} className="pivot-col-total">
                              <div className="pivot-cell-content">
                                <div className="pivot-cell-count">{colTotals[timeType].count}</div>
                                <div className="pivot-cell-time">{formatTime(colTotals[timeType].totalTime)}</div>
                                <div className="pivot-cell-time" style={{ fontSize: '0.85em', opacity: 0.8 }}>SOLL: {formatSollzeit(colTotals[timeType].totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</div>
                              </div>
                            </td>
                          ))}
                          <td className="pivot-grand-total">
                            <div className="pivot-cell-content">
                              <div className="pivot-cell-count">{grandTotal.count}</div>
                              <div className="pivot-cell-time">{formatTime(grandTotal.totalTime)}</div>
                              <div className="pivot-cell-time" style={{ fontSize: '0.85em', opacity: 0.8 }}>SOLL: {formatSollzeit(grandTotal.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="pivot-legend">
                    <div className="pivot-legend-item">
                      <span className="pivot-legend-label">Anzahl</span>
                      <span className="pivot-legend-desc">Anzahl der Messpunkte</span>
                    </div>
                    <div className="pivot-legend-item">
                      <span className="pivot-legend-label">Gesamtzeit</span>
                      <span className="pivot-legend-desc">Summe der Einzelzeiten</span>
                    </div>
                    <div className="pivot-legend-item">
                      <span className="pivot-legend-label">Ø Leistungsgrad</span>
                      <span className="pivot-legend-desc">Durchschnittlicher Leistungsgrad</span>
                    </div>
                    <div className="pivot-legend-hint">
                      💡 <strong>Tipp:</strong> Klicken Sie auf eine Zelle, um die einzelnen Messpunkte zu sehen
                    </div>
                  </div>
                </div>
                    );
                  })()}
                  
                  {/* x__ Zeitarten am Ende für Pivot */}
                  {analysisViewMode === 'pivot' && Object.keys(analysis.excludedTimeTypes || {}).length > 0 && (() => {
                    const excludedTimeTypes = Object.keys(analysis.excludedTimeTypes || {}).sort();
                    let excludedGrandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
                    excludedTimeTypes.forEach(timeType => {
                      const data = analysis.excludedTimeTypes[timeType];
                      excludedGrandTotal.count += data.count;
                      excludedGrandTotal.totalTime += data.totalTime;
                      excludedGrandTotal.totalVorgabeTime += (data.totalVorgabeTime || 0);
                      excludedGrandTotal.grades.push(...data.grades);
                    });
                    const getTimeTypeDescription = (timeTypeName) => {
                      const timeTypeObj = defaultTimeTypes.find(tt => tt.name === timeTypeName);
                      return timeTypeObj ? timeTypeObj.description : '';
                    };
                    
                    return (
                      <div className="analysis-table-wrapper" style={{ marginTop: '30px' }}>
                        <h4 style={{ marginBottom: '10px', color: darkMode ? '#f9fafb' : '#111827' }}>Nicht zu verwendende Zeit (x__)</h4>
                        <table className="analysis-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                          <thead>
                            <tr style={{ backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>
                              <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Zeitart</th>
                              <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Beschreibung</th>
                              <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Anzahl</th>
                              <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Gesamtzeit (IST)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {excludedTimeTypes.map(timeType => {
                              const data = analysis.excludedTimeTypes[timeType];
                              const description = getTimeTypeDescription(timeType);
                              return (
                                <tr 
                                  key={timeType}
                                  style={{ cursor: data.measurements && data.measurements.length > 0 ? 'pointer' : 'default' }}
                                  onClick={() => {
                                    if (data.measurements && data.measurements.length > 0) {
                                      setPivotDrillDown({ timeType, measurements: data.measurements });
                                    }
                                  }}
                                  title={data.measurements && data.measurements.length > 0 ? `Klicken für Details: ${data.count} Messpunkt(e)` : ''}
                                  onMouseEnter={(e) => {
                                    if (data.measurements && data.measurements.length > 0) {
                                      e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', fontWeight: 'bold', color: darkMode ? '#e5e7eb' : '#111827' }}>{timeType}</td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>{description || '-'}</td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{data.count}</td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(data.totalTime)}</td>
                                </tr>
                              );
                            })}
                            <tr style={{ backgroundColor: darkMode ? '#1f2937' : '#e5e7eb', fontWeight: 'bold' }}>
                              <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>Gesamt</td>
                              <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}></td>
                              <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{excludedGrandTotal.count}</td>
                              <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(excludedGrandTotal.totalTime)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="pivot-drilldown">
                  <div className="drilldown-header">
                    <h4>
                      {pivotDrillDown.activity ? `${pivotDrillDown.activity} × ${pivotDrillDown.timeType}` : 
                       pivotDrillDown.timeType ? `Zeitart: ${pivotDrillDown.timeType}` :
                       pivotDrillDown.kuerzel ? `Kürzel: ${pivotDrillDown.kuerzel}` : 'Details'}
                    </h4>
                    <div className="drilldown-summary">
                      <span>{pivotDrillDown.measurements.length} Messpunkt(e)</span>
                      <span>Gesamtzeit (IST): {formatTime(pivotDrillDown.measurements.reduce((sum, m) => sum + m.individualTime, 0))}</span>
                      <span>Vorgabezeit (SOLL): {formatTime(pivotDrillDown.measurements.reduce((sum, m) => sum + (m.vorgabeTime || 0), 0))}</span>
                    </div>
                  </div>
                  <div className="drilldown-measurements">
                    {pivotDrillDown.measurements.map((m, idx) => (
                      <div key={idx} className="drilldown-item">
                        <div className="drilldown-item-header">
                          <span className="drilldown-number">#{m.number}</span>
                          <span className="drilldown-grade">Leistungsgrad: {m.grade}%</span>
                          <span className="drilldown-time">IST: {formatTime(m.individualTime)}</span>
                          {m.vorgabeTime && (
                            <span className="drilldown-time" style={{ fontSize: '0.9em', opacity: 0.8 }}>SOLL: {formatTime(m.vorgabeTime)}</span>
                          )}
                        </div>
                        <div className="drilldown-item-details">
                          {m.timestamp && (
                            <span>Zeitpunkt: {new Date(m.timestamp).toLocaleString('de-DE')}</span>
                          )}
                          {m.note && (
                            <span className="drilldown-note">Notiz: {m.note}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
}

export default AnalysisPanel;
